import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { Button } from "../components/Button";

export default function Index() {
  const { user, role, isLoading, signOut } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && role && !isLoading) {
      navigate(`/${role}/dashboard`, { replace: true });
    }
  }, [user, role, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <header className="bg-white dark:bg-gray-800 shadow">
        <div className="container mx-auto px-4 py-6 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Logistics Buddy</h1>
          {user ? (
            <div className="flex items-center space-x-4">
              <Link to={`/${role}/dashboard`}>
                <Button variant="outline" size="sm">Dashboard</Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={signOut}>Sign Out</Button>
            </div>
          ) : (
            <Link to="/auth">
              <Button variant="primary" size="sm">Sign In</Button>
            </Link>
          )}
        </div>
      </header>

      <main className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-extrabold text-gray-900 dark:text-white sm:text-5xl">
            Welcome to Logistics Buddy
          </h2>
          <p className="mt-4 text-xl text-gray-600 dark:text-gray-400">
            The ultimate platform for connecting drivers and dispatchers for efficient logistics management.
          </p>

          <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-2">
            <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden">
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">For Drivers</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Find and accept jobs, manage your schedule, and track your earnings all in one place.
                </p>
                <div className="mt-6">
                  <Link to={user ? "/driver/dashboard" : "/auth"}>
                    <Button variant="primary" className="w-full">
                      {user ? "Go to Driver Dashboard" : "Sign In as Driver"}
                    </Button>
                  </Link>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden">
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">For Dispatchers</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Create job requests, assign drivers, and manage your logistics operations efficiently.
                </p>
                <div className="mt-6">
                  <Link to={user ? "/dispatcher/dashboard" : "/auth"}>
                    <Button variant="primary" className="w-full">
                      {user ? "Go to Dispatcher Dashboard" : "Sign In as Dispatcher"}
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <footer className="bg-white dark:bg-gray-800 shadow-inner">
        <div className="container mx-auto px-4 py-8">
          <p className="text-center text-gray-500 dark:text-gray-400">
            &copy; {new Date().getFullYear()} Logistics Buddy. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
